// @ts-nocheck — admin page, types added in a later sub-step
import React from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import {
  listUsers,
  createUser,
  updateUser,
  deactivateUser,
  listMarketCatalog,
  detectMarketsForOwner,
  listHubspotOwners,
  canonicalUserSlug,
  LEVELS,
  LEVEL_LABELS_EN,
} from "../lib/data";
import type { User, MarketCatalogEntry, HubspotOwner } from "../lib/data";
import { useViewer } from "../lib/data/viewer";
// Phase 3b — adopt v2 primitives for the form modal + inline form markup.
import Modal from "../components/ui/Modal";
import V2Field from "../components/ui/Field";
import Form from "../components/ui/Form";
import Input from "../components/ui/Input";
import V2Select from "../components/ui/Select";
import V2Button from "../components/ui/Button";
// Phase 3e — section-level empty/error/loading states use primitives.
import EmptyState from "../components/ui/EmptyState";
import ErrorState from "../components/ui/ErrorState";
import Skeleton from "../components/ui/Skeleton";
// Phase 4b audit — DR-23 (DataTable) compliance.
import DataTable from "../components/ui/DataTable";
// Phase 4b audit — DR-14: wrap title= buttons in v2 Tooltip.
import Tooltip from "../components/ui/Tooltip";
// Phase B1 — layout primitive.
import Page from "../components/ui/Page";
// Phase 4e — IT/EN parity. AdminUsers was English-only; reads the global
// lang preference set by ForecastItaly's lang toggle.
import { useLang } from "../lib/lang";
// Hotfix (2026-05-16) — shared validated worker URL resolver.
import { readWorkerUrl } from "../lib/worker-url";

/* src/views/AdminUsers.tsx — /admin/users view (Phase 5.5).

   READS are open per §1.4 (anyone with the view password sees the
   directory). WRITES require both the ADMIN role on the viewer AND
   the legacy EDIT password (admin-gated routes per §1.6).

   UX
     - Anyone past the view-password gate can land here and see the
       list (informational).
     - ADMIN viewers see a "Unlock admin actions" button. Clicking it
       prompts for the edit password (stored in component state for
       the session — NOT localStorage).
     - Once unlocked: Create button + Edit/Deactivate per row appear.
     - Non-ADMIN viewers see the list but no write affordances. Per
       §1.5, identity metadata writes are ADMIN only.

   Soft-identity reminder (architecture.md §1.6): clicking through
   the picker doesn't VERIFY identity. The double gate (X-User-Id role
   + X-Edit-Password) on writes is the real guard. The worker (Phase
   5.6) will enforce; for now we trust+verify on the frontend.
*/

const { useState, useEffect, useMemo } = React;

// readWorkerUrl moved to ../lib/worker-url.ts (allowlist + self-heal).

function readViewPw() {
  try { return localStorage.getItem("forecast_view_pw") || ""; }
  catch { return ""; }
}

const EMPTY_FORM = {
  name: "",
  email: "",
  level: "AE",
  label: "",
  // Phase 5.5d — multi-market via checkbox grid sourced from HubSpot
  // catalog (/api/markets/catalog). Stored as an array of slugs.
  marketIds: ["italy"] as string[],
  hubspotOwnerId: "",
  commissionPlanRef: "",
  isAdmin: false,
  active: true,
};

export default function AdminUsers() {
  // Guard: this view assumes the user has already passed the view-password
  // gate via /forecast/italy. If they hit /admin/users directly without a
  // password in localStorage, redirect them to the forecast page first.
  // (A future refactor lifts the password gate to App-level so all routes
  // benefit; that's Phase 5+ scope.)
  if (!readViewPw()) {
    return <Navigate to="/forecast/italy" replace />;
  }

  const { viewer, realViewer, isWatching, startWatching } = useViewer();
  const navigate = useNavigate();
  const lang = useLang();
  const [users, setUsers] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [editPw, setEditPw] = useState(""); // session-only; not persisted
  const [pwInput, setPwInput] = useState("");
  const [pwError, setPwError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formMode, setFormMode] = useState("create"); // "create" | "edit"
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState(null);
  const [busy, setBusy] = useState(false);
  // Phase 5.5d — markets catalog (from HubSpot via worker). null = loading,
  // [] = loaded but empty (worker fallback should prevent this).
  const [marketCatalog, setMarketCatalog] = useState<MarketCatalogEntry[] | null>(null);
  const [catalogError, setCatalogError] = useState<string | null>(null);
  // Phase 6.1b — HubSpot owners list (for the owner-id dropdown).
  const [hubspotOwners, setHubspotOwners] = useState<HubspotOwner[] | null>(null);
  const [hubspotOwnersError, setHubspotOwnersError] = useState<string | null>(null);

  // Phase 6.2 — admin affordances follow the REAL session identity, not
  // the effective viewer. So an admin watching-as a non-admin still sees
  // the admin column, the unlock pane, and the Watch buttons. Writes are
  // an extra step harder: `unlocked` is gated on !isWatching so the
  // create/edit/deactivate UI stays disabled until they Exit.
  const isAdmin = !!realViewer?.isAdmin;
  const unlocked = isAdmin && editPw.length > 0 && !isWatching;

  // Load users on mount + after any successful write.
  const reload = async () => {
    setLoadError(null);
    try {
      const list = await listUsers({ workerUrl: readWorkerUrl(), viewPassword: readViewPw() });
      const levelPriority = { EXEC: 0, MID: 1, AE: 2 };
      const sorted = [...list].sort((a, b) => {
        if (a.active !== b.active) return a.active ? -1 : 1;
        const pa = levelPriority[a.level] ?? 99;
        const pb = levelPriority[b.level] ?? 99;
        if (pa !== pb) return pa - pb;
        return (a.name || "").localeCompare(b.name || "");
      });
      setUsers(sorted);
    } catch (e) {
      setLoadError(e?.message || String(e));
    }
  };
  useEffect(() => { reload(); }, []);

  // Load market catalog on mount. Falls back to whatever the worker returns
  // on HubSpot failure (italy + global), so the form is always usable.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const cat = await listMarketCatalog({
          workerUrl: readWorkerUrl(),
          viewPassword: readViewPw(),
        });
        if (!cancelled) setMarketCatalog(cat);
      } catch (e: any) {
        if (!cancelled) {
          setCatalogError(e?.message || String(e));
          // Last-resort UI fallback so admins can still pick *something*.
          setMarketCatalog([
            { slug: "italy",  label: "Italy" },
            { slug: "global", label: "Global" },
          ]);
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Phase 6.1b — load HubSpot owners for the dropdown. Failure is non-fatal
  // (form falls back to letting admins paste the raw ID).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await listHubspotOwners({
          workerUrl: readWorkerUrl(),
          viewPassword: readViewPw(),
        });
        if (!cancelled) setHubspotOwners(list);
      } catch (e: any) {
        if (!cancelled) {
          setHubspotOwnersError(e?.message || String(e));
          setHubspotOwners([]);
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // ─── Unlock admin actions ─────────────────────────────────────────────
  const handleUnlock = (e) => {
    e.preventDefault();
    if (!pwInput) return;
    setEditPw(pwInput);
    setPwInput("");
    setPwError(null);
  };

  // ─── Form ──────────────────────────────────────────────────────────────
  const openCreate = () => {
    setFormMode("create");
    setFormData(EMPTY_FORM);
    setFormError(null);
    setSuggestNote(null);
    setShowForm(true);
  };
  const openEdit = (u) => {
    setFormMode("edit");
    setFormData({
      id: u.id,
      name: u.name,
      email: u.email,
      level: u.level,
      label: u.label || "",
      marketIds: Array.isArray(u.marketIds) ? [...u.marketIds] : [],
      hubspotOwnerId: u.hubspotOwnerId || "",
      commissionPlanRef: u.commissionPlanRef || "",
      isAdmin: !!u.isAdmin,
      active: u.active,
    });
    setFormError(null);
    setSuggestNote(null);
    setShowForm(true);
  };

  // ─── Markets checkbox helpers ─────────────────────────────────────────
  const toggleMarket = (slug: string) => {
    setFormData(prev => {
      const has = prev.marketIds.includes(slug);
      return {
        ...prev,
        marketIds: has ? prev.marketIds.filter(s => s !== slug) : [...prev.marketIds, slug],
      };
    });
  };
  const selectAllMarkets = () => {
    setFormData(prev => ({
      ...prev,
      marketIds: (marketCatalog || []).map(m => m.slug),
    }));
  };
  const clearAllMarkets = () => {
    setFormData(prev => ({ ...prev, marketIds: [] }));
  };

  // Phase 5.5e — auto-detect markets from the user's HubSpot deals.
  // Uses formData.hubspotOwnerId (the field above in the form) so the admin
  // sees the suggestion update if they change the owner id. Adds detected
  // slugs to the existing selection (union — non-destructive).
  const [suggestBusy, setSuggestBusy] = useState(false);
  const [suggestNote, setSuggestNote] = useState<string | null>(null);
  const suggestFromHubspot = async () => {
    const ownerId = (formData.hubspotOwnerId || "").trim();
    if (!ownerId) {
      setSuggestNote("Set a HubSpot owner ID first to suggest from deals.");
      return;
    }
    setSuggestBusy(true);
    setSuggestNote(null);
    try {
      const detected = await detectMarketsForOwner({
        workerUrl: readWorkerUrl(),
        viewPassword: readViewPw(),
        ownerId,
      });
      if (detected.length === 0) {
        setSuggestNote(`No deals found for HubSpot owner ${ownerId}.`);
      } else {
        setFormData(prev => {
          const merged = new Set(prev.marketIds);
          for (const m of detected) merged.add(m.slug);
          return { ...prev, marketIds: Array.from(merged) };
        });
        const summary = detected
          .map(m => `${m.label} (${m.dealCount})`)
          .join(", ");
        setSuggestNote(`Added from HubSpot: ${summary}.`);
      }
    } catch (e: any) {
      setSuggestNote(`Suggest failed: ${e?.message || String(e)}`);
    } finally {
      setSuggestBusy(false);
    }
  };
  const closeForm = () => { setShowForm(false); setFormError(null); setSuggestNote(null); };

  const submitForm = async (e) => {
    e.preventDefault();
    setBusy(true);
    setFormError(null);
    try {
      const opts = { workerUrl: readWorkerUrl(), viewPassword: readViewPw(), editPassword: editPw, viewerUserId: viewer?.id };
      const marketIds = Array.isArray(formData.marketIds) ? formData.marketIds : [];
      if (marketIds.length === 0) {
        setFormError("Pick at least one market.");
        setBusy(false);
        return;
      }
      const payload = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        level: formData.level,
        label: formData.label.trim(),
        marketIds,
        hubspotOwnerId: formData.hubspotOwnerId.trim() || undefined,
        commissionPlanRef: formData.commissionPlanRef.trim() || undefined,
        isAdmin: !!formData.isAdmin,
        active: !!formData.active,
      };
      if (formMode === "create") {
        await createUser(viewer, payload, opts);
      } else {
        await updateUser(viewer, formData.id, payload, opts);
      }
      setShowForm(false);
      await reload();
    } catch (e) {
      // Worker returns 403 on wrong edit-password; clear our cached pw + bounce back to unlock.
      if (e?.status === 403) {
        setEditPw("");
        setPwError("Admin password rejected. Re-unlock to continue.");
      }
      setFormError(e?.message || String(e));
    } finally {
      setBusy(false);
    }
  };

  // ─── Deactivate ────────────────────────────────────────────────────────
  const handleDeactivate = async (u) => {
    if (!confirm(`Deactivate ${u.name} (${u.email})? Records stay in KV; user just becomes hidden.`)) return;
    setBusy(true);
    try {
      await deactivateUser(viewer, u.id, {
        workerUrl: readWorkerUrl(),
        viewPassword: readViewPw(),
        editPassword: editPw,
        viewerUserId: viewer?.id,
      });
      await reload();
    } catch (e) {
      if (e?.status === 403) {
        setEditPw("");
        setPwError("Admin password rejected. Re-unlock to continue.");
      } else {
        alert(`Deactivate failed: ${e?.message || String(e)}`);
      }
    } finally {
      setBusy(false);
    }
  };

  // ─── Render ────────────────────────────────────────────────────────────
  // Phase A2: page title + ViewerChip + cross-page links now live in the
  // global AppTopbar / AppSidebar. The local <header> block was removed —
  // navigation between admin views happens via the sidebar.
  // Phase B1: outer wrapper graduated to <Page>.
  return (
    <Page>
      {isWatching && (
        <div style={{
          marginBottom: "var(--space-4)",
          padding: "var(--space-3) var(--space-4)",
          background: "var(--warn-soft)",
          border: "1px solid var(--warn)",
          borderRadius: "var(--radius-md)",
          fontSize: "var(--fs-body-sm)",
          color: "var(--text-primary)",
        }}>
          👁 {lang === "it"
            ? <>Stai osservando come <b>{viewer?.name}</b>. Le scritture sono disabilitate finché non esci (chip nell'header).</>
            : <>You're watching as <b>{viewer?.name}</b>. Writes are disabled until you exit (chip in the header).</>}
        </div>
      )}

      {/* Admin-actions unlock — only shown to ADMIN viewers.
          Phase 4b audit — DR-21: rebuilt as <Form> + <V2Field> + <Input>
          + <V2Button>. The horizontal layout is wrapped in a Card-like
          surround instead of inline form CSS. */}
      {isAdmin && !unlocked && !isWatching && (
        <Form
          onSubmit={handleUnlock}
          style={{
            display: "flex",
            gap: "var(--space-2)",
            alignItems: "flex-end",
            marginBottom: "var(--space-4)",
            padding: "var(--space-3) var(--space-4)",
            background: "var(--bg-2)",
            border: "1px solid var(--border)",
            borderRadius: "var(--radius-md)",
          }}
        >
          <V2Field
            label={lang === "it"
              ? "Password admin per sbloccare crea/modifica/disattiva"
              : "Admin password to unlock create/edit/deactivate"}
            htmlFor="admin-unlock-pw"
            error={pwError || undefined}
            style={{ flex: 1, marginBottom: 0 }}
          >
            <Input
              id="admin-unlock-pw"
              kind="password"
              value={pwInput}
              onChange={setPwInput}
              autoFocus
            />
          </V2Field>
          <V2Button type="submit" variant="primary" size="md" disabled={!pwInput}>
            {lang === "it" ? "Sblocca" : "Unlock"}
          </V2Button>
        </Form>
      )}

      {/* Create button — only when unlocked */}
      {unlocked && (
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "var(--space-4)" }}>
          <V2Button variant="primary" size="md" onClick={openCreate}>
            {lang === "it" ? "+ Nuovo utente" : "+ New user"}
          </V2Button>
        </div>
      )}

      {/* Table — Phase 3e: section-level states use primitives */}
      {loadError && (
        <ErrorState
          lang={lang}
          title={lang === "it" ? "Impossibile caricare gli utenti" : "Couldn't load users"}
          message={loadError}
          onRetry={reload}
          retryLabel={lang === "it" ? "Riprova" : "Retry"}
        />
      )}
      {users === null && !loadError && (
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)", padding: "var(--space-2) 0" }}>
          <Skeleton variant="block" height={36} />
          <Skeleton variant="block" height={36} />
          <Skeleton variant="block" height={36} />
        </div>
      )}
      {users && users.length === 0 && (
        <EmptyState
          title={lang === "it" ? "Nessun utente" : "No users yet"}
          message={lang === "it"
            ? <>Esegui <code>npm run seed:users</code> dalla root del progetto per popolare la directory.</>
            : <>Run <code>npm run seed:users</code> from the project root to seed the directory.</>}
        />
      )}
      {users && users.length > 0 && (
        // Phase 4b audit — DR-23: wrap in v2 DataTable. Default compact
        // density matches the audit table; sticky header off (this view is
        // shorter than viewport in practice and the surround/scroll context
        // doesn't pin cleanly).
        <DataTable density="compact" stickyHeader={false}>
          <thead>
            <tr>
              <th>{lang === "it" ? "Nome" : "Name"}</th>
              <th>Email</th>
              <th>{lang === "it" ? "Livello" : "Level"}</th>
              <th>{lang === "it" ? "Ruolo" : "Label"}</th>
              <th>{lang === "it" ? "Mercati" : "Markets"}</th>
              <th>{lang === "it" ? "Owner HS" : "HS Owner"}</th>
              {/* Admin column is HIDDEN from non-admins per Marco's instruction. */}
              {isAdmin && <th>Admin</th>}
              <th>{lang === "it" ? "Attivo" : "Active"}</th>
              {isAdmin && <th style={{ textAlign: "right" }}>{lang === "it" ? "Osserva" : "Watch"}</th>}
              {unlocked && <th style={{ textAlign: "right" }}>{lang === "it" ? "Azioni" : "Actions"}</th>}
            </tr>
          </thead>
          <tbody>
            {users.map(u => (
              <tr key={u.id} style={{ opacity: u.active ? 1 : 0.5 }}>
                <td>
                  <Link to={`/u/${canonicalUserSlug(u)}`} style={{ color: "var(--text-primary)", textDecoration: "none", fontWeight: "bold" }}>
                    {u.name}
                  </Link>
                </td>
                <td style={{ fontFamily: "var(--font-mono)" }}>{u.email}</td>
                <td>{u.level}</td>
                <td>{u.label}</td>
                <td>{Array.isArray(u.marketIds) ? u.marketIds.join(", ") : ""}</td>
                <td style={{ fontFamily: "var(--font-mono)" }}>{u.hubspotOwnerId || "—"}</td>
                {isAdmin && <td>{u.isAdmin ? "✓" : ""}</td>}
                <td>{u.active ? (lang === "it" ? "sì" : "yes") : (lang === "it" ? "no" : "no")}</td>
                {isAdmin && (
                  <td style={{ textAlign: "right" }}>
                    {/* Hide Watch button for own row and inactive users.
                        Phase 4b audit (DR-14) — native title= migrated to
                        <Tooltip>; explains the disabled state for keyboard +
                        touch users where native title is invisible. */}
                    {u.active && u.id !== realViewer?.id && (
                      <Tooltip tip={isWatching
                        ? (lang === "it" ? "Esci prima dalla modalità watch attuale" : "Exit current watch first")
                        : (lang === "it" ? `Visualizza come ${u.name} (sola lettura)` : `Render as ${u.name} (read-only)`)}>
                        <V2Button
                          variant="ghost"
                          size="sm"
                          onClick={async () => {
                            try {
                              await startWatching(u.id);
                              navigate(`/u/${canonicalUserSlug(u)}`);
                            } catch (e: any) { alert(`${lang === "it" ? "Watch fallito" : "Watch failed"}: ${e?.message || String(e)}`); }
                          }}
                          disabled={busy || isWatching}
                        >
                          👁 {lang === "it" ? "Osserva" : "Watch"}
                        </V2Button>
                      </Tooltip>
                    )}
                  </td>
                )}
                {unlocked && (
                  <td style={{ textAlign: "right" }}>
                    <V2Button variant="ghost" size="sm" onClick={() => openEdit(u)} disabled={busy} style={{ marginRight: "var(--space-1)" }}>
                      {lang === "it" ? "Modifica" : "Edit"}
                    </V2Button>
                    {u.active && (
                      <V2Button variant="ghost" size="sm" tone="warn" onClick={() => handleDeactivate(u)} disabled={busy}>
                        {lang === "it" ? "Disattiva" : "Deactivate"}
                      </V2Button>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </DataTable>
      )}

      {/* Phase 3b — form modal migrated to v2 Modal (hideActions; the form
          owns its own Form.Actions for busy-state-aware buttons). Fields use
          v2 Field + Input/Select. The Markets checkbox grid stays as custom
          markup inside a V2Field wrapper because it's a domain-specific
          control (not just a labeled input). */}
      <Modal
        open={showForm}
        onClose={closeForm}
        size="md"
        hideActions
        lang={lang}
        title={formMode === "create"
          ? (lang === "it" ? "Nuovo utente" : "New user")
          : (lang === "it" ? "Modifica utente" : "Edit user")}
      >
        <Form onSubmit={submitForm}>
          <V2Field label={lang === "it" ? "Nome" : "Name"} htmlFor="au-name" required>
            <Input id="au-name" value={formData.name} onChange={(v: any) => setFormData({...formData, name: v})} />
          </V2Field>
          <V2Field label="Email" htmlFor="au-email" required>
            <Input id="au-email" kind="email" value={formData.email} onChange={(v: any) => setFormData({...formData, email: v})} />
          </V2Field>
          <V2Field
            label={lang === "it" ? "Livello" : "Level"}
            htmlFor="au-level"
            required
            hint={lang === "it" ? "Definisce l'ambito di responsabilità." : "Drives ownership scope."}
          >
            <V2Select
              id="au-level"
              value={formData.level}
              options={LEVELS.map(l => ({ id: l, label: `${l} — ${LEVEL_LABELS_EN[l]}` }))}
              onChange={(v: any) => setFormData({...formData, level: v})}
            />
          </V2Field>
          <V2Field
            label={lang === "it" ? "Ruolo" : "Label"}
            htmlFor="au-label"
            required
            hint={lang === "it"
              ? "Titolo visualizzato, es. 'GTM Lead', 'CCO', 'Sales Lead'."
              : "Display title, e.g. 'GTM Lead', 'CCO', 'Sales Lead'."}
          >
            <Input id="au-label" value={formData.label} onChange={(v: any) => setFormData({...formData, label: v})} />
          </V2Field>
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <span style={{ fontSize: "var(--fs-body-sm)", color: "var(--text-muted)" }}>
                    {lang === "it" ? "Mercati" : "Markets"} <span style={{ color: "var(--danger)" }}>*</span>
                    <span style={{ color: "var(--text-muted)" }}>
                      {lang === "it"
                        ? <> — letti dalla proprietà deal HubSpot <code>country_of_origin</code></>
                        : <> — pulled from HubSpot deal property <code>country_of_origin</code></>}
                    </span>
                  </span>
                  <div style={{ display: "flex", gap: "var(--space-2)" }}>
                    {/* Phase 4b audit (DR-14) — native title= migrated to
                        <Tooltip>; the hint here explains the disabled
                        state when no HubSpot owner ID is set. */}
                    <Tooltip tip={!(formData.hubspotOwnerId || "").trim()
                      ? (lang === "it" ? "Imposta prima un owner ID HubSpot" : "Set a HubSpot owner ID first")
                      : (lang === "it" ? "Rileva i mercati dai deal di questo owner" : "Detect markets from this owner's deals")}>
                      <V2Button
                        variant="ghost"
                        size="sm"
                        onClick={suggestFromHubspot}
                        disabled={suggestBusy || !(formData.hubspotOwnerId || "").trim()}
                        loading={suggestBusy}
                      >
                        {suggestBusy
                          ? (lang === "it" ? "Rilevamento…" : "Detecting…")
                          : (lang === "it" ? "Suggerisci da HubSpot" : "Suggest from HubSpot")}
                      </V2Button>
                    </Tooltip>
                    <V2Button variant="ghost" size="sm" onClick={selectAllMarkets} disabled={!marketCatalog || marketCatalog.length === 0}>
                      {lang === "it" ? "Seleziona tutti" : "Select all"}
                    </V2Button>
                    <V2Button variant="ghost" size="sm" onClick={clearAllMarkets} disabled={formData.marketIds.length === 0}>
                      {lang === "it" ? "Pulisci" : "Clear"}
                    </V2Button>
                  </div>
                </div>
                {marketCatalog === null ? (
                  <div style={{ fontSize: "var(--fs-body-sm)", color: "var(--text-muted)", padding: "var(--space-2)" }}>
                    {lang === "it" ? "Caricamento mercati…" : "Loading markets…"}
                  </div>
                ) : marketCatalog.length === 0 ? (
                  <div style={{ fontSize: "var(--fs-body-sm)", color: "var(--danger)", padding: "var(--space-2)" }}>
                    {lang === "it" ? "HubSpot non ha restituito mercati." : "No markets returned by HubSpot."}
                  </div>
                ) : (
                  <div style={{
                    display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
                    gap: "var(--space-1) var(--space-3)",
                    padding: "var(--space-3)",
                    border: "1px solid var(--border)", borderRadius: "var(--radius-sm)",
                    maxHeight: 200, overflowY: "auto", background: "var(--bg-2)",
                  }}>
                    {marketCatalog.map(m => (
                      <label key={m.slug} style={{ display: "flex", alignItems: "center", gap: "var(--space-2)", cursor: "pointer", fontSize: "var(--fs-body-sm)" }}>
                        <input
                          type="checkbox"
                          checked={formData.marketIds.includes(m.slug)}
                          onChange={() => toggleMarket(m.slug)}
                        />
                        <span>{m.label}</span>
                      </label>
                    ))}
                  </div>
                )}
                {catalogError && (
                  <div style={{ fontSize: "var(--fs-body-sm)", color: "var(--text-muted)" }}>
                    {lang === "it"
                      ? `(Fetch catalogo fallito — mostro il fallback. ${catalogError})`
                      : `(Catalog fetch failed — showing fallback. ${catalogError})`}
                  </div>
                )}
                {suggestNote && (
                  <div style={{ fontSize: "var(--fs-body-sm)", color: "var(--text-muted)" }}>
                    {suggestNote}
                  </div>
                )}
              </div>
          <V2Field
            label={lang === "it" ? "Owner HubSpot" : "HubSpot owner"}
            htmlFor="au-hsowner"
            hint={lang === "it" ? "Scegli dalla lista live (facoltativo)." : "Pick from the live list (optional)."}
            error={hubspotOwnersError
              ? (lang === "it"
                  ? `Fetch owner fallito: ${hubspotOwnersError}. Incolla qui sotto l'ID numerico se lo conosci.`
                  : `Owners fetch failed: ${hubspotOwnersError}. Paste the numeric ID below if you know it.`)
              : undefined}
          >
            {hubspotOwners === null ? (
              <span style={{ fontSize: "var(--fs-body-sm)", color: "var(--text-muted)", padding: "var(--space-2)" }}>
                {lang === "it" ? "Caricamento owner…" : "Loading owners…"}
              </span>
            ) : (
              <V2Select
                id="au-hsowner"
                value={formData.hubspotOwnerId}
                onChange={(v: any) => setFormData({...formData, hubspotOwnerId: v})}
                options={[
                  { id: "", label: lang === "it" ? "— nessuno —" : "— none —" },
                  // Preserve a manual / unknown ID if the loaded list doesn't include it.
                  ...(formData.hubspotOwnerId && !hubspotOwners.find((o: any) => o.id === formData.hubspotOwnerId)
                    ? [{ id: formData.hubspotOwnerId, label: lang === "it"
                        ? `${formData.hubspotOwnerId} (non nella lista HubSpot)`
                        : `${formData.hubspotOwnerId} (not in HubSpot list)` }]
                    : []),
                  ...hubspotOwners.map((o: any) => ({ id: o.id, label: `${o.name}${o.email ? ` — ${o.email}` : ""}` })),
                ]}
              />
            )}
            {hubspotOwnersError && (
              <Input
                value={formData.hubspotOwnerId}
                onChange={(v: any) => setFormData({...formData, hubspotOwnerId: v})}
                placeholder={lang === "it" ? "ID owner HubSpot numerico" : "Numeric HubSpot owner ID"}
              />
            )}
          </V2Field>
          <V2Field
            label={lang === "it" ? "Riferimento piano provvigioni" : "Commission plan ref"}
            htmlFor="au-cpref"
            hint={lang === "it" ? "Facoltativo." : "Optional."}
          >
            <Input id="au-cpref" value={formData.commissionPlanRef} onChange={(v: any) => setFormData({...formData, commissionPlanRef: v})} />
          </V2Field>
          {/* Checkboxes — Field's slot works fine but the checkbox itself
              stays native. Phase 4 may introduce a <Checkbox> primitive;
              for now we render the checkbox inline beside the label text. */}
          <label style={{ display: "flex", alignItems: "center", gap: "var(--space-2)", fontSize: "var(--fs-body-sm)" }}>
            <input type="checkbox" checked={formData.isAdmin} onChange={(e) => setFormData({...formData, isAdmin: e.target.checked})} />
            <span>Admin <span style={{ color: "var(--text-muted)" }}>
              {lang === "it" ? "— concede CRUD utenti + rotazione password" : "— grants user CRUD + password rotation"}
            </span></span>
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: "var(--space-2)", fontSize: "var(--fs-body-sm)" }}>
            <input type="checkbox" checked={formData.active} onChange={(e) => setFormData({...formData, active: e.target.checked})} />
            <span>{lang === "it" ? "Attivo" : "Active"}</span>
          </label>
          {formError && (
            <div className="v2-field__error" role="alert">{formError}</div>
          )}
          <Form.Actions>
            <V2Button variant="ghost" onClick={closeForm} disabled={busy}>
              {lang === "it" ? "Annulla" : "Cancel"}
            </V2Button>
            <V2Button type="submit" variant="primary" disabled={busy} loading={busy}>
              {formMode === "create"
                ? (lang === "it" ? "Crea" : "Create")
                : (lang === "it" ? "Salva" : "Save")}
            </V2Button>
          </Form.Actions>
        </Form>
      </Modal>
    </Page>
  );
}

// Phase 3b — local Field helper removed; the v2 Field + Input primitives
// from src/components/ui/ replace it. Inline-style label + input markup
// gone from this view.
